import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Switch,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { 
  X, 
  Calendar, 
  FileText, 
  Package, 
  Clock,
  Euro,
  User,
  Building2,
  Plus,
  Search,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { getToken, base_url } from "../config/config";

const AnfrageForm = () => {
  // -----------------------------
  // Year & counters for ANF
  // -----------------------------
  const currentYear = new Date().getFullYear().toString().slice(-2);

  const [counter1, setCounter1] = useState("1000");
  const [counter2, setCounter2] = useState("00");
  const [counter3, setCounter3] = useState("00");

  // -----------------------------
  // Form data
  // -----------------------------
  const [formData, setFormData] = useState({
    ANF: "",
    Kunden: "",
    estimatedTime: "",
    estimatedPrice: "",
    Auftragsbezeichnung: "",
    Menge: "",
    Artikelnummer: "",
    EstimatedMaterialCost: "",
    MaxLohnaufwand: "",
    Baugruppenzeichnung: "",
    Erläuterung: "",
    // New fields for simplified mode
    deadline: "",
    beschreibung: "",
    isComplete: true,
    // New fields as requested
    anfrageEingang: new Date().toISOString().split('T')[0],
    anfrage: "",
    kalkulation: "",
    angebotNr: "",
    angebotErstelltDate: "",
    angebotGeschickt: "",
    firma: "",
    knr: "",
    angebotAngenommen: false,
    agNr: "",
  });

  // -----------------------------
  // Aufgabe creation fields
  // -----------------------------
  const [createAufgabe, setCreateAufgabe] = useState(false);
  const [angebotErstellt, setAngebotErstellt] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [formMode, setFormMode] = useState("simplified"); // Start with simplified

  // -----------------------------
  // Kunden selection logic
  // -----------------------------
  const [KundenList, setKundenList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKunden, setSelectedKunden] = useState(null);
  const [showKundenModal, setShowKundenModal] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // -----------------------------
  // Loading / Error states
  // -----------------------------
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const token = getToken();

  // -----------------------------
  // useEffect: Fetch existing Anfragen and determine the initial counters
  // -----------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const anfrageResponse = await fetch(`${base_url}/v1/getAllAnfrag`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const anfrageData = await anfrageResponse.json();

        let newANF = `1000-00-00-${currentYear}`;

        if (
          anfrageData &&
          anfrageData.anfrag &&
          anfrageData.anfrag.length > 0
        ) {
          const currentYearAnfrag = anfrageData.anfrag.filter((item) => {
            const parts = item.ANF.split("-");
            return parts[3] === currentYear;
          });

          if (currentYearAnfrag.length > 0) {
            currentYearAnfrag.sort((a, b) => {
              const aParts = a.ANF.split("-");
              const bParts = b.ANF.split("-");
              const a1 = parseInt(aParts[0], 10);
              const a2 = parseInt(aParts[1], 10);
              const a3 = parseInt(aParts[2], 10);
              const b1 = parseInt(bParts[0], 10);
              const b2 = parseInt(bParts[1], 10);
              const b3 = parseInt(bParts[2], 10);

              if (a1 !== b1) return a1 - b1;
              if (a2 !== b2) return a2 - b2;
              return a3 - b3;
            });

            const lastANF = currentYearAnfrag[currentYearAnfrag.length - 1].ANF;
            const parts = lastANF.split("-");
            if (parts.length === 4) {
              let newCounter1 = parts[0];
              let newCounter2 = parts[1];
              let newCounter3 = parseInt(parts[2], 10) + 1;
              if (newCounter3 > 99) {
                newCounter3 = 0;
                newCounter2 = parseInt(newCounter2, 10) + 1;
                if (newCounter2 > 99) {
                  newCounter2 = 0;
                  newCounter1 = parseInt(newCounter1, 10) + 1;
                }
                newCounter2 = String(newCounter2).padStart(2, "0");
              }
              newCounter1 = String(newCounter1).padStart(4, "0");
              newCounter3 = String(newCounter3).padStart(2, "0");

              newANF = `${newCounter1}-${newCounter2}-${newCounter3}-${currentYear}`;

              setCounter1(newCounter1);
              setCounter2(newCounter2);
              setCounter3(newCounter3);
            }
          }
        } else {
          setCounter1("1000");
          setCounter2("00");
          setCounter3("00");
        }

        setFormData((prev) => ({ ...prev, ANF: newANF }));

        const suppliersResponse = await fetch(`${base_url}/v1/getKunden`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const suppliersData = await suppliersResponse.json();

        if (suppliersData && suppliersData.data) {
          setKundenList(suppliersData.data);
        } else {
          setKundenList([]);
        }

        const employeesResponse = await fetch(`${base_url}/v1/getAllEmployees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const employeesData = await employeesResponse.json();

        if (employeesData && Array.isArray(employeesData)) {
          setEmployees(employeesData);
        } else {
          setEmployees([]);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, [currentYear]);

  // -----------------------------
  // Whenever counters change, update the full ANF
  // -----------------------------
  useEffect(() => {
    const newANF = `${counter1}-${counter2}-${counter3}-${currentYear}`;
    setFormData((prev) => ({ ...prev, ANF: newANF }));
  }, [counter1, counter2, counter3, currentYear]);

  // -----------------------------
  // Increment functions for each counter
  // -----------------------------
  const incrementCounter1 = () => {
    let newVal = parseInt(counter1, 10) + 1;
    setCounter1(String(newVal).padStart(5, "0"));
  };

  const incrementCounter2 = () => {
    let newVal = parseInt(counter2, 10) + 1;
    if (newVal > 99) newVal = 0;
    setCounter2(String(newVal).padStart(2, "0"));
  };

  const incrementCounter3 = () => {
    let newVal = parseInt(counter3, 10) + 1;
    if (newVal > 99) newVal = 0;
    setCounter3(String(newVal).padStart(2, "0"));
  };

  // -----------------------------
  // Form field change handler
  // -----------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  
  // Handle form mode change - set default values for simplified mode
  useEffect(() => {
    if (formMode === "simplified") {
      setFormData(prev => ({
        ...prev,
        estimatedTime: prev.estimatedTime || "0",
        estimatedPrice: prev.estimatedPrice || "0",
        Menge: prev.Menge || "1",
        isComplete: false
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        isComplete: true
      }));
    }
  }, [formMode]);

  // -----------------------------
  // Kunden modal logic
  // -----------------------------
  const filteredKunden = useMemo(() => {
    if (!searchTerm) return [];
    return KundenList.filter(
      (lf) =>
        (lf.bezeichnung || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (lf.kurzbezeichnung || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    );
  }, [KundenList, searchTerm]);

  const handleSelectKunden = useCallback((lf) => {
    setSelectedKunden(lf);
    setSearchTerm(lf.bezeichnung);
    setConfirmed(false);
  }, []);

  const handleClearKunden = useCallback(() => {
    setSelectedKunden(null);
    setSearchTerm("");
    setConfirmed(false);
  }, []);

  const handleConfirmKunden = useCallback(() => {
    if (selectedKunden) {
      setConfirmed(true);
      setFormData((prev) => ({
        ...prev,
        Kunden: selectedKunden._id,
      }));
    }
    setShowKundenModal(false);
  }, [selectedKunden]);

  // -----------------------------
  // Handle form mode change
  // -----------------------------
  const handleFormModeChange = (event, newMode) => {
    if (newMode !== null) {
      setFormMode(newMode);
    }
  };

  // -----------------------------
  // Create an Aufgabe
  // -----------------------------
  const createNewAufgabe = async (anfrageId) => {
    try {
      const aufgabeData = {
        title: `Anfrage ${formData.ANF}`,
        description: angebotErstellt ? "Angebot erstellt" : "Neue Anfrage",
        user: selectedEmployeeId,
        userModel: "Employee",
        isTask: true,
        status: "assigned",
      };

      const response = await fetch(`${base_url}/v1/createAufgabe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(aufgabeData),
      });

      if (!response.ok) {
        throw new Error("Failed to create Aufgabe");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating Aufgabe:", error);
      throw error;
    }
  };

  // -----------------------------
  // Submit form
  // -----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Add selected employee to formData if task is being created
      const submissionData = { ...formData };
      if (createAufgabe && selectedEmployeeId) {
        submissionData.assignedEmployee = selectedEmployeeId;
      }

      const response = await fetch(`${base_url}/v1/createAnfrag`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submissionData),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Success:", data);
        setError("");

        if (createAufgabe && selectedEmployeeId) {
          try {
            await createNewAufgabe(data.anfrag._id);
            console.log("Aufgabe created successfully");
          } catch (aufgabeError) {
            console.error("Failed to create Aufgabe:", aufgabeError);
            setError("Anfrage created, but failed to create Aufgabe.");
          }
        }

        window.location.reload();
      } else {
        console.error("Error:", response.statusText);
        setError("Failed to submit form. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to submit form. Please try again.");
    }
  };

  // -----------------------------
  // Modal for Kunden selection
  // -----------------------------
  const renderKundenModal = () => {
    if (!showKundenModal) return null;
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)',
        }}
      >
        <Card sx={{ 
          width: '100%', 
          maxWidth: 500, 
          m: 2,
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                <Search sx={{ mr: 1, verticalAlign: 'middle' }} />
                Kunde auswählen
              </Typography>
              <IconButton
                onClick={() => setShowKundenModal(false)}
                sx={{ color: 'text.secondary' }}
              >
                <X />
              </IconButton>
            </Box>

            {!selectedKunden ? (
              <Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Suche nach Kunden..."
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                {searchTerm && filteredKunden.length > 0 && (
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      maxHeight: 300, 
                      overflow: 'auto',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    {filteredKunden.map((lf) => (
                      <Box
                        key={lf._id}
                        onClick={() => handleSelectKunden(lf)}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                          '&:last-child': { borderBottom: 'none' }
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {lf.bezeichnung}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lf.kurzbezeichnung}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
                    <CheckCircle2 sx={{ mr: 1 }} />
                    Ausgewählter Kunde
                  </Typography>
                  <Button
                    variant="text"
                    onClick={handleClearKunden}
                    size="small"
                  >
                    Zurücksetzen
                  </Button>
                </Box>
                <Paper sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {selectedKunden.bezeichnung}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Kurzbezeichnung: {selectedKunden.kurzbezeichnung}
                  </Typography>
                </Paper>
                {!confirmed && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleConfirmKunden}
                    size="large"
                    sx={{ 
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1.1rem'
                    }}
                  >
                    Auswahl bestätigen
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  // -----------------------------
  // Render
  // -----------------------------
  if (loading) {
    return (
      <Container sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center",
        minHeight: "60vh"
      }}>
        <CircularProgress size={60} thickness={4} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: '1.5rem' }
          }}
          icon={<AlertCircle />}
        >
          {error}
        </Alert>
      )}

      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          borderRadius: 3,
          color: 'white'
        }}
      >
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
          Kunden-Anfrage
        </Typography>
        
        {/* Form mode selector */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Paper elevation={3} sx={{ borderRadius: 6, overflow: 'hidden' }}>
            <Tabs
              value={formMode}
              onChange={handleFormModeChange}
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  px: 4,
                  py: 1.5,
                },
                '& .Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                }
              }}
            >
              <Tab value="simplified" label="Einfache Anfrage" />
              <Tab value="full" label="Vollständige Anfrage" />
            </Tabs>
          </Paper>
        </Box>

        {/* ANF Number Display */}
        <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(10px)', borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              ANF Nummer
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mb: 2 }}>
              <TextField
                size="small"
                value={counter1}
                onChange={(e) => setCounter1(e.target.value.padStart(4, '0'))}
                sx={{ 
                  width: 80,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'white' },
                  }
                }}
                inputProps={{ 
                  maxLength: 4, 
                  style: { textAlign: 'center', color: 'white' } 
                }}
              />
              <Typography sx={{ mx: 1, color: 'white' }}>-</Typography>
              <TextField
                size="small"
                value={counter2}
                onChange={(e) => setCounter2(e.target.value.padStart(2, '0'))}
                sx={{ 
                  width: 60,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'white' },
                  }
                }}
                inputProps={{ 
                  maxLength: 2, 
                  style: { textAlign: 'center', color: 'white' } 
                }}
              />
              <Typography sx={{ mx: 1, color: 'white' }}>-</Typography>
              <TextField
                size="small"
                value={counter3}
                onChange={(e) => setCounter3(e.target.value.padStart(2, '0'))}
                sx={{ 
                  width: 60,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'white' },
                  }
                }}
                inputProps={{ 
                  maxLength: 2, 
                  style: { textAlign: 'center', color: 'white' } 
                }}
              />
              <Typography sx={{ mx: 1, color: 'white' }}>-</Typography>
              <Chip 
                label={currentYear} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 600 }}
              />
            </Box>
            <Stack direction="row" spacing={1} justifyContent="center">
              <IconButton onClick={incrementCounter1} sx={{ color: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }}>
                <Plus size={16} />
              </IconButton>
              <IconButton onClick={incrementCounter2} sx={{ color: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }}>
                <Plus size={16} />
              </IconButton>
              <IconButton onClick={incrementCounter3} sx={{ color: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }}>
                <Plus size={16} />
              </IconButton>
            </Stack>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, textAlign: 'center' }}>
              Klicken Sie auf + oder bearbeiten Sie die Felder direkt
            </Typography>
          </CardContent>
        </Card>
      </Paper>

      {/* Main Form */}
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {/* ANF Field */}
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="ANF Nummer"
                  name="ANF"
                  value={formData.ANF}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <FileText sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              {/* Kunden Selection */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Kunde *
                  </Typography>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setShowKundenModal(true)}
                    sx={{
                      justifyContent: "flex-start",
                      p: 2,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      borderStyle: 'dashed',
                      '&:hover': { borderStyle: 'solid' }
                    }}
                    startIcon={<Building2 />}
                  >
                    {selectedKunden
                      ? `${selectedKunden.bezeichnung} (${selectedKunden.kurzbezeichnung || ""})`
                      : "Kunde auswählen"}
                  </Button>
                </Box>
              </Grid>

              {/* Conditional Fields based on Form Mode */}
              {formMode === "simplified" ? (
                <>
                  {/* Simplified Mode Fields */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Beschreibung"
                      name="beschreibung"
                      value={formData.beschreibung}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      variant="outlined"
                      placeholder="Kurze Beschreibung der Anfrage..."
                      InputProps={{
                        startAdornment: <FileText sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Stichtag"
                      name="deadline"
                      type="date"
                      value={formData.deadline}
                      onChange={handleChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: <Calendar sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Menge"
                      name="Menge"
                      type="number"
                      value={formData.Menge}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Package sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  {/* Full Mode Fields */}
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Auftragsbezeichnung"
                      name="Auftragsbezeichnung"
                      value={formData.Auftragsbezeichnung}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Baugruppenzeichnung"
                      name="Baugruppenzeichnung"
                      value={formData.Baugruppenzeichnung}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      multiline
                      rows={4}
                      label="Erläuterung"
                      name="Erläuterung"
                      value={formData.Erläuterung}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Geschätzte Zeit (Stunden)"
                      name="estimatedTime"
                      type="number"
                      value={formData.estimatedTime}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Clock sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Geschätzter Preis (€)"
                      name="estimatedPrice"
                      type="number"
                      value={formData.estimatedPrice}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Euro sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Menge"
                      name="Menge"
                      type="number"
                      value={formData.Menge}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Package sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Artikelnummer"
                      name="Artikelnummer"
                      value={formData.Artikelnummer}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Geschätzte Materialkosten (€)"
                      name="EstimatedMaterialCost"
                      type="number"
                      value={formData.EstimatedMaterialCost}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Euro sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Max. Lohnaufwand (€)"
                      name="MaxLohnaufwand"
                      type="number"
                      value={formData.MaxLohnaufwand}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Euro sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  {/* New Fields Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                      Angebotsinformationen
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Anfrage Eingang"
                      name="anfrageEingang"
                      type="date"
                      value={formData.anfrageEingang}
                      onChange={handleChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: <Calendar sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Anfrage"
                      name="anfrage"
                      value={formData.anfrage}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Kalkulation"
                      name="kalkulation"
                      value={formData.kalkulation}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Angebot - Nr."
                      name="angebotNr"
                      value={formData.angebotNr}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Angebot erstellt (Datum)"
                      name="angebotErstelltDate"
                      type="date"
                      value={formData.angebotErstelltDate}
                      onChange={handleChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: <Calendar sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Angebot geschickt"
                      name="angebotGeschickt"
                      type="date"
                      value={formData.angebotGeschickt}
                      onChange={handleChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: <Calendar sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Firma"
                      name="firma"
                      value={formData.firma}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <Building2 sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Knr."
                      name="knr"
                      value={formData.knr}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.angebotAngenommen}
                          onChange={(e) => setFormData(prev => ({ ...prev, angebotAngenommen: e.target.checked }))}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        />
                      }
                      label="Angebot angenommen"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="AG - Nr."
                      name="agNr"
                      value={formData.agNr}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          <Divider />

          {/* Task Creation Section */}
          <Box sx={{ p: 4, bgcolor: 'grey.50' }}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <User sx={{ mr: 1 }} />
                  Aufgabe erstellen
                </Typography>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createAufgabe}
                      onChange={(e) => setCreateAufgabe(e.target.checked)}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                    />
                  }
                  label="Aufgabe mit dieser Anfrage erstellen"
                  sx={{ mb: 2 }}
                />
                
                {createAufgabe && (
                  <Box sx={{ ml: 4, space: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={angebotErstellt}
                          onChange={(e) => setAngebotErstellt(e.target.checked)}
                        />
                      }
                      label="Angebot erstellt"
                      sx={{ mb: 2 }}
                    />
                    
                    <FormControl fullWidth>
                      <InputLabel>Mitarbeiter auswählen</InputLabel>
                      <Select
                        value={selectedEmployeeId}
                        onChange={(e) => setSelectedEmployeeId(e.target.value)}
                        label="Mitarbeiter auswählen"
                        required={createAufgabe}
                      >
                        {employees.map((employee) => (
                          <MenuItem key={employee._id} value={employee._id}>
                            {employee.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          <Divider />

          {/* Submit Section */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                py: 2,
                px: 6,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1.2rem',
                fontWeight: 600,
                background: 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
                boxShadow: '0 3px 5px 2px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #0d47a1 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 10px 4px rgba(25, 118, 210, 0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Anfrage absenden
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Kunden Selection Modal */}
      {renderKundenModal()}
    </Container>
  );
};

export default AnfrageForm;